import { create } from "zustand";
import {
  applyMove,
  applyTurn,
  createInitialState,
  defaultVictoryConfig,
  finalizeTurn,
  legalDestinationsAllModes,
  squareKey,
  type Capture,
  type GameState,
  type Move,
  type MoveMode,
  type PendingTurn,
  type Side,
  type Square,
  type VictoryConfig,
} from "../engine";
import * as gamesApi from "../games/api";
import type { NetClient } from "../net/client";
import { newSessionId, setGameUrl } from "./route";
import { clearSession, saveSession } from "./session";

export type GameMode = "local" | "bot" | "online" | "tutorial";

export interface OnlineSessionMeta {
  client: NetClient;
  mySide: Side;
  code: string;
  rejoinToken: string;
}

export interface UiState {
  game: GameState;
  mode: GameMode;
  /** For PvB: which side the human plays. */
  humanSide: "white" | "black";

  selected: Square | null;
  /**
   * Map of selectable destinations from the selected square, with the mode (Pyramid only).
   * Key: squareKey(destination)
   */
  destinations: Map<string, MoveMode | undefined>;

  /** Set after applyMove; cleared after finalizeTurn. */
  pending: PendingTurn | null;

  /** Animation queue. */
  animations: AnimationStep[];

  /** Last completed turn's captures, for HUD display. */
  lastCaptures: Capture[];

  /** Stack of previous game states (most recent on top). Used by undo. */
  history: GameState[];

  /** Toggle for the in-game hint surface (only meaningful in mode === "bot"). */
  hintsEnabled: boolean;

  online: OnlineSessionMeta | null;

  /** Server-side game record id, for saving the move log to D1. null if anonymous or recording failed. */
  gameRecordId: string | null;
  /**
   * Generation counter that increments on every newGame so an in-flight createGameRecord
   * response can't race a freshly-started game and assign its id to the wrong session.
   */
  gameRecordGeneration: number;

  /** Client-minted 32-hex id keying this game's URL and localStorage entry. */
  sessionId: string | null;

  // Actions
  newGame: (config: { mode: GameMode; humanSide?: "white" | "black"; victoryConfig?: VictoryConfig; initialState?: GameState; online?: OnlineSessionMeta; sessionId?: string }) => void;
  /** Drop the persisted session for the current game (called when exiting to the menu). */
  endSession: () => void;
  selectSquare: (sq: Square | null) => void;
  proposeMove: (move: Move) => void;
  declareCapture: (capture: Capture, advance: boolean) => void;
  skipCaptures: () => void;
  applyExternalTurn: (move: Move, declared?: Capture, advance?: boolean) => void; // for bot/network
  setRemoteGame: (game: GameState) => void;
  /** Online: GameRoom sends gameRecordId on joined/state once D1 row exists. Mirror into store. */
  setOnlineGameRecordId: (id: string) => void;
  undoLastTurn: () => void;
  toggleHints: () => void;
  ackAnimation: (id: number) => void;
}

export interface AnimationStep {
  id: number;
  kind: "slide" | "captureFlash" | "captureLabel";
  from?: Square;
  to?: Square;
  square?: Square;
  /** For captureLabel: text to render (e.g. "4 + 5 = 9", "12 = 12", "siege"). */
  text?: string;
  startedAt: number;
}

let animSeq = 0;
const nextAnimId = () => ++animSeq;
let gameRecordGenerationSeq = 0;
const nextGameRecordGeneration = () => ++gameRecordGenerationSeq;

/**
 * Lead time (ms) added to a slide animation's startedAt. Between the moment we queue the
 * animation and the first frame the browser paints with the new state, ~1 frame's worth of
 * time elapses; without a lead the first interpolated draw would land a pixel or two past
 * `from`, producing a visible jerk against the previous static draw at `from`. Adding a small
 * lead clamps `progress()` to 0 for the first paint so the piece begins exactly at `from` and
 * then slides smoothly.
 */
const SLIDE_LEAD_MS = 32;

function destinationsFor(state: GameState, sq: Square): Map<string, MoveMode | undefined> {
  const map = new Map<string, MoveMode | undefined>();
  for (const opt of legalDestinationsAllModes(state, sq)) {
    map.set(squareKey(opt.to), opt.mode);
  }
  return map;
}

/**
 * Best-effort POST of a just-finalized turn to the server. Called from declareCapture,
 * skipCaptures, and applyExternalTurn after the engine state is committed to the store.
 * Any failure is silently swallowed — the in-memory game is the source of truth, and the
 * server's saved log is purely additive history.
 *
 * Must be called AFTER `set({ game: result.state, ... })` so `get().game.turnNumber` already
 * reflects the just-completed turn.
 */
function recordTurnEffect(
  get: () => UiState,
  args: { side: Side; move: Move; declared?: Capture | null; advance?: boolean },
): void {
  const state = get();
  const gameId = state.gameRecordId;
  if (!gameId) return;
  // Capture the generation at issue time so a parallel newGame can't have us mistake one
  // game's turn for another's later in the promise chain.
  const generation = state.gameRecordGeneration;
  const turnNumber = state.game.turnNumber;
  void gamesApi
    .appendMoveRecord(gameId, {
      turnNumber,
      side: args.side,
      move: args.move,
      declared: args.declared ?? null,
      advance: !!args.advance,
    })
    .then(() => {
      // If the game just ended on that move, push the end record too.
      const after = get();
      if (after.gameRecordGeneration !== generation) return;
      if (after.gameRecordId !== gameId) return;
      const victory = after.game.victory;
      if (!victory) return;
      const whiteFinalScore = after.game.captured.white.reduce((acc, p) => acc + p.value, 0);
      const blackFinalScore = after.game.captured.black.reduce((acc, p) => acc + p.value, 0);
      void gamesApi.endGameRecord(gameId, {
        winnerSide: victory.side,
        victoryKind: victory.level,
        victoryDetail: victory.detail,
        whiteFinalScore,
        blackFinalScore,
      });
    });
}

export const useGame = create<UiState>((set, get) => ({
  game: createInitialState(),
  mode: "local",
  humanSide: "white",
  selected: null,
  destinations: new Map(),
  pending: null,
  animations: [],
  lastCaptures: [],
  history: [],
  hintsEnabled: false,
  online: null,
  gameRecordId: null,
  gameRecordGeneration: 0,
  sessionId: null,

  newGame: ({ mode, humanSide = "white", victoryConfig, initialState, online, sessionId: providedSessionId }) => {
    const generation = nextGameRecordGeneration();
    const effectiveVictoryConfig = victoryConfig ?? defaultVictoryConfig();
    // Tutorial mode is scripted and shouldn't take over the URL or get a persistent session id.
    // Everything else: if we're restoring from a /g/<id> URL, keep that id; otherwise mint one.
    const sessionId =
      mode === "tutorial" ? null : providedSessionId ?? newSessionId();
    if (sessionId) setGameUrl(sessionId);
    const startingState = initialState ?? createInitialState(effectiveVictoryConfig);
    set({
      game: startingState,
      mode,
      humanSide,
      selected: null,
      destinations: new Map(),
      pending: null,
      animations: [],
      lastCaptures: [],
      history: [],
      hintsEnabled: false,
      online: online ?? null,
      gameRecordId: null,
      gameRecordGeneration: generation,
      sessionId,
    });
    // The auto-save subscribe below fires on this set() (game changed) and persists the
    // initial position for non-tutorial modes — no need to duplicate the write here.

    // Bot games of a signed-in user get recorded server-side. Best-effort: if there's no
    // session token, or the network call fails, we just won't save this one. The generation
    // check keeps a stale create response from clobbering a freshly-started game's record id.
    if (mode === "bot") {
      const isThresholdLevel =
        effectiveVictoryConfig.level === "common" || effectiveVictoryConfig.level === "proper";
      void gamesApi
        .createGameRecord({
          mode: "bot",
          victoryLevel: effectiveVictoryConfig.level,
          threshold: isThresholdLevel ? effectiveVictoryConfig.commonThreshold : null,
          humanSide,
        })
        .then((result) => {
          if (!result) return;
          if (get().gameRecordGeneration !== generation) return;
          // Upgrade the URL + localStorage key from the client sessionId to the D1
          // gameRecordId so /g/<id> resolves cross-device for signed-in users.
          const cur = get().sessionId;
          if (cur && cur !== result.gameId) clearSession(cur);
          set({ gameRecordId: result.gameId, sessionId: result.gameId });
          setGameUrl(result.gameId);
        });
    }
  },

  setOnlineGameRecordId: (id) => {
    if (get().gameRecordId === id) return;
    // Same upgrade as bot mode: swap the URL/storage key to the server's authoritative id.
    const cur = get().sessionId;
    if (cur && cur !== id) clearSession(cur);
    set({ gameRecordId: id, sessionId: id });
    setGameUrl(id);
  },

  selectSquare: (sq) => {
    const { game, selected, destinations, online, mode, humanSide } = get();
    // Tutorial mode is fully scripted — no user board input
    if (mode === "tutorial") return;
    if (!sq) {
      set({ selected: null, destinations: new Map() });
      return;
    }

    // In online mode, only let the player act on their side's turn
    if (online && game.toMove !== online.mySide) return;
    // In bot mode, only let the human act on their turn
    if (mode === "bot" && game.toMove !== humanSide) return;

    // If clicking a destination of the currently selected piece → propose a move
    if (selected && destinations.has(squareKey(sq))) {
      const m = destinations.get(squareKey(sq));
      const move: Move = m ? { from: selected, to: sq, mode: m } : { from: selected, to: sq };
      get().proposeMove(move);
      return;
    }

    // Otherwise, attempt to select a piece on the square (must be the side to move)
    const piece = game.board.get(squareKey(sq));
    if (piece && piece.side === game.toMove && !game.victory) {
      set({ selected: sq, destinations: destinationsFor(game, sq) });
      return;
    }

    // Click on empty/unselectable square clears selection
    set({ selected: null, destinations: new Map() });
  },

  proposeMove: (move) => {
    const { game, animations } = get();
    let pending: PendingTurn;
    try {
      pending = applyMove(game, move);
    } catch (e) {
      console.warn("invalid move", e);
      return;
    }

    // Queue slide animation immediately
    const slideId = nextAnimId();
    const now = performance.now();
    const slideStart = now + SLIDE_LEAD_MS;
    const newAnims = [
      ...animations,
      { id: slideId, kind: "slide" as const, from: move.from, to: move.to, startedAt: slideStart },
    ];
    // Queue flash + math label for any siege-captured squares
    for (const siege of pending.sieges) {
      newAnims.push({ id: nextAnimId(), kind: "captureFlash" as const, square: siege.target, startedAt: now });
      newAnims.push({ id: nextAnimId(), kind: "captureLabel" as const, square: siege.target, text: siege.formula, startedAt: now });
    }

    set({
      pending,
      selected: null,
      destinations: new Map(),
      animations: newAnims,
    });

    // If no declarable captures, finalize immediately.
    if (pending.declarable.length === 0) {
      get().skipCaptures();
    }
  },

  declareCapture: (capture, advance) => {
    const { pending, animations, online, mode, humanSide, game, history } = get();
    if (!pending) return;
    // Only the active player (us, in online/bot modes) may finalize.
    if (online && pending.side !== online.mySide) return;
    if (mode === "bot" && pending.side !== humanSide) return;

    let result;
    try {
      result = finalizeTurn(pending, capture, { advance });
    } catch (e) {
      console.warn("invalid declared capture", e);
      return;
    }

    const now = performance.now();

    // If the player is advancing into the vacated square, the moving piece's slide animation needs
    // its endpoint extended (or a new slide added) so the piece visibly slides to its final
    // resting square rather than teleporting there.
    let baseAnims: AnimationStep[] = animations;
    if (advance && (capture.method === "encounter" || capture.method === "deceit") && capture.attackers.length === 1) {
      const attackerSq = capture.attackers[0];
      const slideIdx = baseAnims.findIndex((a) => a.kind === "slide");
      const slideAnim = slideIdx >= 0 ? baseAnims[slideIdx] : null;
      const slideToMatchesAttacker =
        slideAnim?.to && slideAnim.to.file === attackerSq.file && slideAnim.to.rank === attackerSq.rank;

      if (slideAnim && slideToMatchesAttacker) {
        // Moving piece IS the attacker — extend the existing slide to land on the capture target.
        baseAnims = baseAnims.map((a, i) => (i === slideIdx ? { ...a, to: capture.target } : a));
      } else {
        // A different piece is the attacker — queue a separate slide from its position to the target.
        baseAnims = [
          ...baseAnims,
          {
            id: nextAnimId(),
            kind: "slide" as const,
            from: attackerSq,
            to: capture.target,
            startedAt: now + SLIDE_LEAD_MS,
          },
        ];
      }
    }

    set({
      game: result.state,
      history: [...history, game],
      pending: null,
      lastCaptures: [...pending.sieges, capture],
      animations: [
        ...baseAnims,
        { id: nextAnimId(), kind: "captureFlash", square: capture.target, startedAt: now },
        { id: nextAnimId(), kind: "captureLabel", square: capture.target, text: capture.formula, startedAt: now },
      ],
    });

    recordTurnEffect(get, { side: pending.side, move: pending.move, declared: capture, advance });

    // Send the bundled turn to the server in online mode
    if (online) {
      online.client.send({ type: "move", move: pending.move, declared: capture, advance });
    }
  },

  skipCaptures: () => {
    const { pending, online, mode, humanSide, game, history } = get();
    if (!pending) return;
    if (online && pending.side !== online.mySide) {
      // Opponent's turn skipping captures locally — apply silently, no network send.
      const { state } = finalizeTurn(pending);
      set({ game: state, history: [...history, game], pending: null, lastCaptures: [...pending.sieges] });
      // Don't record opponent moves here in online mode — that's the DurableObject's
      // responsibility (when wired up in Phase 2.5).
      return;
    }
    if (mode === "bot" && pending.side !== humanSide) {
      const { state } = finalizeTurn(pending);
      set({ game: state, history: [...history, game], pending: null, lastCaptures: [...pending.sieges] });
      return;
    }

    const { state } = finalizeTurn(pending);
    set({
      game: state,
      history: [...history, game],
      pending: null,
      lastCaptures: [...pending.sieges],
    });

    recordTurnEffect(get, { side: pending.side, move: pending.move, declared: null, advance: false });

    if (online) {
      online.client.send({ type: "move", move: pending.move });
    }
  },

  applyExternalTurn: (move, declared, advance = false) => {
    const { game, animations, history } = get();
    const sideToRecord: Side = game.toMove;
    let result;
    try {
      result = applyTurn(game, move, { declared, advance });
    } catch (e) {
      console.warn("invalid external turn", e);
      return;
    }

    const now = performance.now();
    // Slide endpoint: if the moving piece is the attacker of a declared advance, extend the slide
    // to the capture target so the piece visibly continues to its final resting square.
    let slideTo = move.to;
    if (
      advance &&
      declared &&
      (declared.method === "encounter" || declared.method === "deceit") &&
      declared.attackers.length === 1 &&
      declared.attackers[0].file === move.to.file &&
      declared.attackers[0].rank === move.to.rank
    ) {
      slideTo = declared.target;
    }

    const slideStart = now + SLIDE_LEAD_MS;
    const newAnims: AnimationStep[] = [
      ...animations,
      { id: nextAnimId(), kind: "slide" as const, from: move.from, to: slideTo, startedAt: slideStart },
    ];

    // For declared captures whose attacker is NOT the moving piece, add a second slide.
    if (
      advance &&
      declared &&
      (declared.method === "encounter" || declared.method === "deceit") &&
      declared.attackers.length === 1 &&
      (declared.attackers[0].file !== move.to.file || declared.attackers[0].rank !== move.to.rank)
    ) {
      newAnims.push({
        id: nextAnimId(),
        kind: "slide" as const,
        from: declared.attackers[0],
        to: declared.target,
        startedAt: slideStart,
      });
    }

    for (const c of [...result.pending.sieges, ...(declared ? [declared] : [])]) {
      newAnims.push({
        id: nextAnimId(),
        kind: "captureFlash" as const,
        square: c.target,
        startedAt: now,
      });
      newAnims.push({
        id: nextAnimId(),
        kind: "captureLabel" as const,
        square: c.target,
        text: c.formula,
        startedAt: now,
      });
    }

    set({
      game: result.state,
      history: [...history, game],
      pending: null,
      lastCaptures: [...result.pending.sieges, ...(declared ? [declared] : [])],
      animations: newAnims,
    });

    // Record bot-mode external turns (the bot's own move) so the saved history is complete.
    // Online mode goes through setRemoteGame instead — the server is authoritative there and
    // DurableObject → D1 wiring lands in Phase 2.5.
    const { mode } = get();
    if (mode === "bot") {
      recordTurnEffect(get, {
        side: sideToRecord,
        move,
        declared: declared ?? null,
        advance,
      });
    }
  },

  setRemoteGame: (game) => {
    set({ game, pending: null, selected: null, destinations: new Map() });
  },

  undoLastTurn: () => {
    const { history, online, mode, humanSide } = get();
    if (history.length === 0) return;
    // Online undo would desync with the server's authoritative state; disabled there.
    // (Tutorial uses this for Back navigation; local + bot can use it from the HUD.)
    if (online) return;

    const newHistory = history.slice();
    let target = newHistory.pop()!;

    // In bot mode, popping a single state lands on the bot's turn — the bot driver would
    // immediately re-play its move, eating the undo. Keep popping until we're back on the
    // human's turn so each undo click reverses one full human↔bot exchange.
    if (mode === "bot") {
      while (target.toMove !== humanSide && newHistory.length > 0) {
        target = newHistory.pop()!;
      }
      // Safety net: if no human-turn state exists (e.g. bot moved first and that's all we have),
      // abort rather than dump the player back into a bot-only turn.
      if (target.toMove !== humanSide) return;
    }

    set({
      game: target,
      history: newHistory,
      pending: null,
      selected: null,
      destinations: new Map(),
      animations: [],
      lastCaptures: [],
    });
  },

  toggleHints: () => {
    set({ hintsEnabled: !get().hintsEnabled });
  },

  ackAnimation: (id) => {
    const { animations } = get();
    set({ animations: animations.filter((a) => a.id !== id) });
  },

  endSession: () => {
    const id = get().sessionId;
    if (id) clearSession(id);
    // Bump the generation so an in-flight createGameRecord .then() can't resurrect
    // sessionId/gameRecordId on a session the user already abandoned.
    set({ sessionId: null, gameRecordId: null, gameRecordGeneration: nextGameRecordGeneration() });
  },
}));

// Auto-persist the live game state to localStorage on every meaningful change. We subscribe
// on the (game, mode, humanSide, online) tuple — the engine state, the mode of play, and the
// online metadata (which is what reconnect needs). Tutorial mode is excluded; everything
// else writes through to /g/<sessionId> in localStorage so a reload restores immediately.
useGame.subscribe((state, prev) => {
  if (!state.sessionId) return;
  if (state.mode === "tutorial") return;
  if (
    state.game === prev.game &&
    state.mode === prev.mode &&
    state.humanSide === prev.humanSide &&
    state.online === prev.online
  ) {
    return;
  }
  saveSession({
    sessionId: state.sessionId,
    mode: state.mode as "local" | "bot" | "online",
    humanSide: state.humanSide,
    victoryConfig: state.game.victoryConfig,
    state: state.game,
    online: state.online
      ? {
          code: state.online.code,
          mySide: state.online.mySide,
          rejoinToken: state.online.rejoinToken,
        }
      : undefined,
  });
});
